"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../ReduxToolkit/Hooks";
import { setExhibition } from "../../ReduxToolkit/Reducer/ExhibitionSlice";
import ExhibitionSwitcherService, {
  type SwitcherResponse,
} from "../../Service/ExhibitionSwitcherService";
import { setActiveExhibition, type StoredExhibition } from "../../utils/exhibitionContext";

/** Format "2026-04-10" → "Apr 10, 2026" */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface Props {
  /** When true only active (status=true) exhibitions are shown.
   *  Superusers see all exhibitions. */
  activeOnly?: boolean;
}

const ExhibitionSwitcherContainer = ({ activeOnly = true }: Props) => {
  const dispatch   = useAppDispatch();
  const navigate   = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [selecting,  setSelecting]  = useState<number | null>(null);
  const [switcher,   setSwitcher]   = useState<SwitcherResponse | null>(null);

  useEffect(() => {
    loadExhibitions();
  }, []);

  const loadExhibitions = async () => {
    setLoading(true);
    try {
      const data = await ExhibitionSwitcherService.getSwitcherList();
      setSwitcher(data);
    } catch (err) {
      toast.error("Failed to load exhibitions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const exhibitions: StoredExhibition[] = !switcher
    ? []
    : activeOnly
    ? switcher.exhibitions.filter((ex) => ex.status)
    : switcher.exhibitions;

  const handleSelect = async (ex: StoredExhibition) => {
    setSelecting(ex.id);
    try {
      dispatch(setExhibition(ex));
      setActiveExhibition(ex); // persist to localStorage — this is what every
                               // API service's X-Exhibition-ID header actually
                               // reads; Redux state alone doesn't reach axios.
      toast.success(`Switched to "${ex.title}"`);
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Failed to switch exhibition.");
      setSelecting(null);
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh", background: "var(--body-color, #f8f9fa)" }}
      >
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Loading exhibitions…</p>
      </div>
    );
  }

  // ── No exhibitions found ───────────────────────────────────────────────────
  if (exhibitions.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "100vh", background: "var(--body-color, #f8f9fa)" }}
      >
        <div className="text-center p-5">
          <i className="fa fa-calendar-times-o fa-4x text-muted mb-3" />
          <h4 className="text-muted">No Active Exhibitions</h4>
          <p className="text-muted">
            There are no active exhibitions at the moment.
            <br />
            Please contact your administrator.
          </p>
          <button
            className="btn btn-outline-primary mt-2"
            onClick={() => navigate("/dashboard", { replace: true })}
          >
            Continue to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Switcher UI ────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
      }}
    >
      {/* ── Header ── */}
      <div className="text-center mb-5">
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 1rem",
            fontSize: "1.8rem",
          }}
        >
          🏛️
        </div>
        <h2 style={{ color: "#fff", fontWeight: 700, marginBottom: "0.5rem" }}>
          Select Exhibition
        </h2>
        <p style={{ color: "rgba(255,255,255,0.6)", maxWidth: 420, margin: "0 auto" }}>
          {exhibitions.length === 1
            ? "One exhibition is currently active."
            : `${exhibitions.length} exhibitions are currently active. Choose the one you want to work in.`}
        </p>
      </div>

      {/* ── Exhibition cards ── */}
      <Container style={{ maxWidth: 960 }}>
        <Row className="justify-content-center g-4">
          {exhibitions.map((ex) => {
            const isCurrent   = switcher?.current_exhibition_id === ex.id;
            const isSelecting = selecting === ex.id;
            const today       = new Date().toISOString().slice(0, 10);
            const isRunning   = ex.start_date <= today && today <= ex.end_date;
            const isUpcoming  = ex.start_date > today;

            return (
              <Col key={ex.id} xs={12} sm={6} lg={exhibitions.length >= 3 ? 4 : 5}>
                <Card
                  style={{
                    borderRadius: 16,
                    border: isCurrent
                      ? "2px solid #4ade80"
                      : "2px solid transparent",
                    background: "rgba(255,255,255,0.07)",
                    backdropFilter: "blur(12px)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    color: "#fff",
                    boxShadow: isCurrent
                      ? "0 0 24px rgba(74,222,128,0.25)"
                      : "0 4px 24px rgba(0,0,0,0.3)",
                  }}
                  onClick={() => !isSelecting && handleSelect(ex)}
                >
                  <CardBody className="p-4">
                    {/* Status badges */}
                    <div className="d-flex gap-2 mb-3 flex-wrap">
                      {ex.status ? (
                        isRunning ? (
                          <Badge
                            style={{
                              background: "rgba(74,222,128,0.2)",
                              color: "#4ade80",
                              border: "1px solid #4ade80",
                              fontWeight: 500,
                            }}
                          >
                            ● Running
                          </Badge>
                        ) : isUpcoming ? (
                          <Badge
                            style={{
                              background: "rgba(250,204,21,0.2)",
                              color: "#facc15",
                              border: "1px solid #facc15",
                              fontWeight: 500,
                            }}
                          >
                            ◎ Upcoming
                          </Badge>
                        ) : (
                          <Badge
                            style={{
                              background: "rgba(148,163,184,0.2)",
                              color: "#94a3b8",
                              border: "1px solid #94a3b8",
                              fontWeight: 500,
                            }}
                          >
                            ✓ Active
                          </Badge>
                        )
                      ) : (
                        <Badge
                          style={{
                            background: "rgba(239,68,68,0.2)",
                            color: "#ef4444",
                            border: "1px solid #ef4444",
                            fontWeight: 500,
                          }}
                        >
                          ✕ Closed
                        </Badge>
                      )}
                      {isCurrent && (
                        <Badge
                          style={{
                            background: "rgba(74,222,128,0.15)",
                            color: "#4ade80",
                            border: "1px solid rgba(74,222,128,0.4)",
                            fontWeight: 500,
                          }}
                        >
                          Current
                        </Badge>
                      )}
                    </div>

                    {/* Title */}
                    <h5
                      style={{
                        fontWeight: 700,
                        marginBottom: "0.25rem",
                        color: "#fff",
                        fontSize: "1.1rem",
                      }}
                    >
                      {ex.title}
                    </h5>

                    {/* Type */}
                    {ex.exhibition_type && (
                      <p
                        style={{
                          color: "rgba(255,255,255,0.5)",
                          fontSize: "0.8rem",
                          marginBottom: "1rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {ex.exhibition_type}
                      </p>
                    )}

                    {/* Details */}
                    <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span>📍</span>
                        <span>{ex.venue}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span>📅</span>
                        <span>
                          {fmtDate(ex.start_date)} – {fmtDate(ex.end_date)}
                        </span>
                      </div>
                    </div>

                    {/* CTA */}
                    <button
                      className="btn w-100 mt-4"
                      disabled={isSelecting}
                      style={{
                        background: isCurrent
                          ? "rgba(74,222,128,0.2)"
                          : "rgba(255,255,255,0.1)",
                        color: isCurrent ? "#4ade80" : "#fff",
                        border: isCurrent
                          ? "1px solid rgba(74,222,128,0.5)"
                          : "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 10,
                        fontWeight: 600,
                        padding: "0.6rem",
                        transition: "all 0.15s",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(ex);
                      }}
                    >
                      {isSelecting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Entering…
                        </>
                      ) : isCurrent ? (
                        "✓ Currently Active"
                      ) : (
                        "Enter Exhibition →"
                      )}
                    </button>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
};

export default ExhibitionSwitcherContainer;